import { Link } from "react-router-dom";
import MarketingShell from "@/components/marketing/MarketingShell";
import { useProductName } from "@/context/BrandContext";
import {
  ArrowRight, Play, Volume2, Bot, BookUser, Ear, GitBranch, Hash,
  Waves, PhoneForwarded, PhoneOff, Flag, Workflow, Braces, Lightbulb,
} from "lucide-react";

const TOC = [
  { id: "overview", label: "Call flow overview" },
  { id: "start", label: "Start" },
  { id: "play-audio", label: "Play Audio" },
  { id: "collect-info", label: "Collect Customer Info" },
  { id: "ai-agent", label: "AI Agent" },
  { id: "wait", label: "Wait for Customer" },
  { id: "customer-response", label: "Customer Response" },
  { id: "dtmf", label: "DTMF" },
  { id: "silence", label: "Silence" },
  { id: "transfer", label: "Transfer" },
  { id: "sip-headers", label: "Transfer SIP headers" },
  { id: "hangup", label: "Hang Up" },
  { id: "end", label: "End" },
  { id: "tips", label: "Design tips" },
];

function Section({ id, icon: Icon, title, color, children }) {
  return (
    <section
      id={id}
      className="scroll-mt-24 rounded-2xl border border-border bg-card/60 p-6 sm:p-8"
      data-testid={`kb-section-${id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-white shadow-sm"
          style={{ background: color }}
        >
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="disp text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2>
          <div className="mt-4 space-y-4 text-[15px] leading-relaxed text-muted-foreground">
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ name, children }) {
  return (
    <div className="rounded-xl border border-border/80 bg-muted/30 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-foreground">{name}</p>
      <p className="mt-1 text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function CodeBlock({ children }) {
  return (
    <pre
      className="overflow-x-auto rounded-xl border border-border bg-slate-950 px-4 py-3 font-mono text-[13px] leading-relaxed text-emerald-300"
      data-testid="kb-code"
    >
      <code>{children}</code>
    </pre>
  );
}

export default function KnowledgeBase() {
  const productName = useProductName();

  return (
    <MarketingShell active="knowledge-base">
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-6 pb-10 pt-10 sm:pt-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">Knowledge Base</p>
          <h1 className="disp mt-2 max-w-3xl text-3xl font-extrabold tracking-tight sm:text-4xl">
            Call flow nodes — how every step works
          </h1>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">
            What each Campaign Designer node does, which settings matter, and how to pass data on
            transfer with SIP headers like{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 text-sm">X-WA-Name={"{name}"}</code>.
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            <a
              href="#sip-headers"
              className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Jump to SIP headers
            </a>
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-primary hover:underline"
            >
              Get started <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-12 lg:grid-cols-[220px_1fr]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">On this page</p>
          <nav className="flex flex-col gap-1" aria-label="Knowledge Base sections">
            {TOC.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className="rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                {item.label}
              </a>
            ))}
          </nav>
        </aside>

        <div className="space-y-6">
          <Section id="overview" icon={Workflow} title="Call flow overview" color="#7c3aed">
            <p>
              A <strong className="text-foreground">call flow</strong> is a visual graph of nodes connected by
              handles (branches). When VICIdial (or a Test Call) delivers a call to your campaign prefix,
              FreeSWITCH runs the flow from <strong className="text-foreground">Start</strong> and follows edges
              until Hang Up / End — or a live Transfer bridges the caller to a human.
            </p>
            <ul className="list-disc space-y-2 pl-5">
              <li>Drag nodes from the left palette onto the canvas.</li>
              <li>Connect an output handle on one node to the top of the next node.</li>
              <li>Nodes with multiple exits (AI Agent, Collect, Transfer, DTMF, …) need every used handle wired.</li>
              <li>Use <strong className="text-foreground">Test Call → Live mic call</strong> in the browser to rehearse the same Live AI path without SIP/CDR.</li>
            </ul>
          </Section>

          <Section id="start" icon={Play} title="Start" color="#22c55e">
            <p>
              Entry point of every campaign. There must be exactly one Start node, and it must connect
              onward (usually to Play Audio, AI Agent, or Collect Customer Info).
            </p>
            <Field name="Settings">None — Start has no configuration.</Field>
            <Field name="How to use">
              Place Start at the top. Connect it to your greeting or AI Agent. Do not leave Start dangling.
            </Field>
          </Section>

          <Section id="play-audio" icon={Volume2} title="Play Audio" color="#6366f1">
            <p>
              Plays a prompt to the caller, then continues to the next connected node. Use it for greetings,
              legal disclosures, busy messages after inactivity, or max-duration follow-ups.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field name="Source — Upload">
                Upload a WAV/audio file staged to FreeSWITCH. Best for fixed brand intros.
              </Field>
              <Field name="Source — Text-to-Speech">
                Type a script and pick a voice. The platform materializes a clip so playback is reliable on the media server.
              </Field>
            </div>
            <Field name="How to use">
              Connect Play Audio after Start or from AI Agent Max duration / Inactivity handles when you want a
              fixed message instead of improvised AI speech. Always wire a next step (Hang Up, End, Transfer, or another node).
            </Field>
          </Section>

          <Section id="collect-info" icon={BookUser} title="Collect Customer Info" color="#0d9488">
            <p>
              Live duplex conversation that asks for enabled fields (name, email, mobile, company, address,
              date of birth, custom) one at a time, confirms answers when configured, and stores clean values
              for later use — especially Transfer SIP headers.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field name="Fields">
                Enable only what you need. Custom fields need a label. Values are available as {"{name}"}, {"{email}"}, {"{mobile}"}, etc.
              </Field>
              <Field name="Language & voice">
                Spoken questions and confirmations use the selected language and TTS voice.
              </Field>
              <Field name="Intro text">
                Optional opening line before the first field question. Leave empty to start asking immediately.
              </Field>
              <Field name="No-reply timeout">
                Seconds of silence after the bot finishes asking. Then the flow takes the Timeout branch (Go to Node).
              </Field>
              <Field name="On timeout">
                Hang up, or Go to Node / Transfer via the Timeout handle. Wire Done and Timeout when using Go to Node.
              </Field>
              <Field name="Confirm answers">
                When on, the agent confirms each value before saving (reduces bad emails/numbers).
              </Field>
            </div>
            <Field name="Handles">
              <strong className="text-foreground">Done</strong> — all enabled fields collected.{" "}
              <strong className="text-foreground">Timeout</strong> — caller stopped answering (connect to Play Audio “you seem busy”, Hang Up, or retry logic).
            </Field>
          </Section>

          <Section id="ai-agent" icon={Bot} title="AI Agent" color="#7c3aed">
            <p>
              Full Live AI conversation node: greets from a call script, follows Instructions (About You,
              global rules, role, conversation rules), can use a Knowledge Base for product facts, and exits
              through allowed action handles or Max duration / Inactivity.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field name="AI Bot / voice / language">
                Which model assignment and spoken language/voice to use for this node.
              </Field>
              <Field name="Personality & role">
                Tone and job (sales, support, qualification, receptionist, …).
              </Field>
              <Field name="Objective">
                One clear goal the agent optimizes for on this node.
              </Field>
              <Field name="Call script">
                Opening IF/ELSE playbook (reception vs owner). The agent starts from this script.
              </Field>
              <Field name="Instructions">
                Structured rules: when a rule matches, speak the line, then run the action (hangup, transfer, collect_info, …).
                Max duration and inactivity are configured in settings below — not as Instruction triggers.
              </Field>
              <Field name="Knowledge Base">
                Optional FAQ/product content the agent must answer from (reduces hallucination).
              </Field>
              <Field name="Maximum Talk Time">
                Cap in minutes. When reached, leave via the <strong className="text-foreground">Max duration</strong> handle (Go to Node).
              </Field>
              <Field name="Customer Inactivity Timeout">
                Seconds of silence after the agent finishes speaking. Then leave via the{" "}
                <strong className="text-foreground">Inactivity</strong> handle.
              </Field>
              <Field name="Allowed actions">
                Checkboxes that create handles: Continue, Collect Customer Information, Transfer, Hang Up, Book Appointment.
                Wire every checked handle.
              </Field>
              <Field name="Pause when customer talks">
                When on, customer barge-in pauses bot audio so the agent listens.
              </Field>
            </div>
            <Field name="Handles">
              Action handles you enabled, plus always <strong className="text-foreground">Max duration</strong> and{" "}
              <strong className="text-foreground">Inactivity</strong>. Typical pattern: Inactivity → Play Audio (“you seem busy”) → Hang Up;
              Max duration → Play Audio (“are you still there?”) → Transfer or Hang Up.
            </Field>
          </Section>

          <Section id="wait" icon={Ear} title="Wait for Customer" color="#f59e0b">
            <p>
              Listens for caller speech (and optionally DTMF) for a timeout window, then branches.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field name="Timeout (seconds)">How long to wait for speech before taking the Silence branch.</Field>
              <Field name="DTMF enabled">Adds a DTMF exit when the caller presses keys.</Field>
            </div>
            <Field name="Handles">
              <strong className="text-foreground">Speech</strong>, <strong className="text-foreground">Silence</strong>,
              and optionally <strong className="text-foreground">DTMF</strong>.
            </Field>
          </Section>

          <Section id="customer-response" icon={GitBranch} title="Customer Response" color="#a855f7">
            <p>
              Classifies what the caller said into named intents (Interested, Not Interested, Callback, Unknown, …)
              using phrase lists, then follows the matching branch.
            </p>
            <Field name="Responses">
              Each response has an id/label and example phrases. Add as many as your script needs. Always include an Unknown fallback.
            </Field>
            <Field name="How to use">
              Place after Wait or after a short Play Audio question. Connect each intent handle to the right next step
              (Transfer for interested, Hang Up for not interested, etc.).
            </Field>
          </Section>

          <Section id="dtmf" icon={Hash} title="DTMF" color="#0ea5e9">
            <p>
              Menu by keypad. Define keys (0–9, *, #); each key is its own output handle.
            </p>
            <Field name="Keys">
              Add digits your IVR needs (e.g. 1 = sales, 2 = support). Connect each key handle to the matching path.
            </Field>
          </Section>

          <Section id="silence" icon={Waves} title="Silence" color="#64748b">
            <p>
              Handles repeated no-answer situations after Wait/Collect-style listening. Use when you want a limited
              number of retries before giving up.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field name="Max attempts">How many silence retries before Max reached.</Field>
              <Field name="Handles">
                <strong className="text-foreground">Retry</strong> (loop back to ask again) and{" "}
                <strong className="text-foreground">Max reached</strong> (Hang Up / Play Audio / End).
              </Field>
            </div>
          </Section>

          <Section id="transfer" icon={PhoneForwarded} title="Transfer" color="#14b8a6">
            <p>
              Warm or blind handoff to a human or another system. Choose <strong className="text-foreground">External SIP Server</strong>{" "}
              or <strong className="text-foreground">DID / telephone number</strong>. Destination is configured on this node only.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field name="SIP — Host">PBX / SIP server hostname or IP (e.g. sip.example.com).</Field>
              <Field name="SIP — Port & transport">Usually 5060 UDP (or TCP/TLS as required by your PBX).</Field>
              <Field name="SIP — Destination / extension">User part of the INVITE (e.g. 1001 or a queue).</Field>
              <Field name="SIP — Auth (optional)">
                Username/password only if the server challenges with 401/407. Leave blank for IP-trusted trunks.
              </Field>
              <Field name="DID">E.164 telephone number when transferring out to the PSTN via your dial plan.</Field>
              <Field name="Handles">
                <strong className="text-foreground">Success</strong> and <strong className="text-foreground">Failure</strong> —
                connect Success to Hang Up/End after a completed bridge; Failure to a fallback prompt or retry.
              </Field>
            </div>
          </Section>

          <Section id="sip-headers" icon={Braces} title="Transfer SIP headers (X-WA-*)" color="#0f766e">
            <p>
              On a SIP Transfer node, <strong className="text-foreground">Additional SIP params</strong> lets you attach
              custom headers to the transfer INVITE — one <code className="rounded bg-muted px-1 text-sm">Header-Name=value</code> per line.
              Values in braces are replaced with fields captured by <strong className="text-foreground">Collect Customer Info</strong>
              (or known lead variables) before the INVITE is sent.
            </p>
            <p className="font-medium text-foreground">Recommended starter set (Campaign Designer → Transfer node):</p>
            <CodeBlock>{`X-WA-Name={name}
X-WA-Email={email}
X-WA-Mobile={mobile}`}</CodeBlock>
            <div className="grid gap-3 sm:grid-cols-3">
              <Field name="X-WA-Name={name}">Caller’s collected name → header for the agent CRM/screen-pop.</Field>
              <Field name="X-WA-Email={email}">Collected email address.</Field>
              <Field name="X-WA-Mobile={mobile}">Collected mobile / phone number.</Field>
            </div>

            <p className="font-medium text-foreground">
              VICIdial inbound dialplan — required on the receiving side
            </p>
            <p>
              Headers alone are not enough for VICIdial agents to see the data. On your{" "}
              <strong className="text-foreground">VICIdial Asterisk inbound dialplan</strong> (the extension /
              DID that the Transfer INVITE lands on), read the <code className="text-sm">X-WA-*</code> SIP headers,
              map them into channel variables, show the name on the agent phone line, stash details in CDR comments,
              and optionally push them onto the lead before the call is queued.
            </p>
            <p>
              Replace <code className="text-sm">111</code> with your real inbound DID / transfer extension.
              Reload the dialplan after saving (<code className="text-sm">dialplan reload</code>).
            </p>
            <CodeBlock>{`exten => 111,1,NoOp(WACalls transfer)
 same => n,Set(WA_NAME=\${SIP_HEADER(X-WA-Name)})
 same => n,Set(WA_EMAIL=\${SIP_HEADER(X-WA-Email)})
 same => n,Set(WA_MOBILE=\${SIP_HEADER(X-WA-Mobile)})
 ; Show name on agent phone line
 same => n,Set(CALLERID(name)=\${WA_NAME})
 ; Put details in Comments (visible on agent screen)
 same => n,Set(CDR(userfield)=Name:\${WA_NAME}|Email:\${WA_EMAIL}|Mobile:\${WA_MOBILE})
 same => n,AGI(agi://127.0.0.1:4577/call_log)
 ; Prefer updating the lead before queueing the agent:
 same => n,AGI(update_lead_custom.agi,\${WA_NAME},\${WA_EMAIL},\${WA_MOBILE})
 same => n,Goto(vicidial-auto,\${EXTEN},1)`}</CodeBlock>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field name="What each line does">
                Pulls <code className="text-sm">X-WA-Name/Email/Mobile</code> from the INVITE, sets{" "}
                <code className="text-sm">CALLERID(name)</code> for the agent phone display, writes a pipe-separated
                summary into <code className="text-sm">CDR(userfield)</code> (Comments on the agent screen), runs
                VICIdial <code className="text-sm">call_log</code>, optionally updates the lead via{" "}
                <code className="text-sm">update_lead_custom.agi</code>, then continues into{" "}
                <code className="text-sm">vicidial-auto</code>.
              </Field>
              <Field name="Custom AGI note">
                <code className="text-sm">update_lead_custom.agi</code> is an example helper — install your own AGI
                (or adapt VICIdial’s lead-update scripts) so name / email / mobile land on the correct lead fields
                before the agent is rung. If you skip the AGI line, agents still see Comments via{" "}
                <code className="text-sm">CDR(userfield)</code>.
              </Field>
            </div>
            <Field name="Other placeholders">
              Any Collect field key works in braces — e.g. <code className="text-sm">{"{company}"}</code>,{" "}
              <code className="text-sm">{"{address}"}</code>, or a custom field key you enabled. Missing values become empty strings.
              Add matching <code className="text-sm">{"Set(WA_…=${SIP_HEADER(X-WA-…)})"}</code> lines in the VICIdial
              dialplan for every extra header you send.
            </Field>
            <Field name="Best practice">
              Collect name/email/mobile (Done → Transfer) before transferring so headers are populated. Confirm the
              Transfer node’s destination matches the VICIdial inbound extension above, then place a live transfer and
              verify the agent screen shows name + Comments (and lead fields if using the AGI).
            </Field>
          </Section>

          <Section id="hangup" icon={PhoneOff} title="Hang Up" color="#ef4444">
            <p>
              Ends the telephony call. Optional reason/label (e.g. <code className="text-sm">not_interested</code>) helps reporting.
            </p>
            <Field name="How to use">
              Connect Hang Up after AI Agent Hang Up handle, Collect Timeout, or Transfer Failure. Then connect Hang Up
              to an <strong className="text-foreground">End</strong> node to mark the flow complete.
            </Field>
          </Section>

          <Section id="end" icon={Flag} title="End" color="#334155">
            <p>
              Terminal marker that the campaign flow finished cleanly. No settings. Place it after Hang Up (or after a
              successful path that should stop the graph).
            </p>
          </Section>

          <Section id="tips" icon={Lightbulb} title="Design tips" color="#ca8a04">
            <ul className="list-disc space-y-2 pl-5">
              <li>Keep the first greeting short; put long FAQs in a Knowledge Base attached to the AI Agent.</li>
              <li>Always wire Max duration and Inactivity on AI Agent — silent callers and long chats need an exit.</li>
              <li>Prefer Collect → Transfer with X-WA-* headers over asking the human agent to re-type details.</li>
              <li>Use Play Audio for legal/compliance lines you must read verbatim; use AI Agent for interactive dialogue.</li>
              <li>Validate the flow in the designer (missing handles block publish) and rehearse with Test Call.</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/features"
                className="inline-flex items-center gap-2 rounded-xl border border-border px-5 py-3 text-sm font-medium hover:bg-muted"
              >
                Features overview
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
              >
                Create an account <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Section>
        </div>
      </div>
    </MarketingShell>
  );
}
